"use client";

import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  useA2UIComponent,
  useA2UIActions,
  useA2UIState,
  ComponentNode,
  type A2UIComponentProps,
  type Types,
} from "@a2ui/react";

const LOADING_TIMEOUT_MS = 15_000;

export const BeeOSButton = memo(function BeeOSButton({
  node,
  surfaceId,
}: A2UIComponentProps<Types.ButtonNode>) {
  const { sendAction } = useA2UIComponent(node, surfaceId);
  const { version } = useA2UIState();
  const { getSurface } = useA2UIActions();
  const props = node.properties;

  const [loading, setLoading] = useState(false);
  const snapshotTreeRef = useRef<unknown>(undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLoading = useCallback(() => {
    setLoading(false);
    snapshotTreeRef.current = undefined;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleClick = useCallback(() => {
    if (loading) return;
    if (props.action) {
      const surface = getSurface(surfaceId);
      snapshotTreeRef.current = surface?.componentTree ?? null;
      setLoading(true);
      sendAction(props.action);

      timerRef.current = setTimeout(clearLoading, LOADING_TIMEOUT_MS);
    }
  }, [props.action, sendAction, loading, getSurface, surfaceId, clearLoading]);

  // Clear loading only when THIS surface's componentTree actually changes.
  // setData (user typing in fields) does NOT rebuild componentTree, so it
  // won't falsely clear loading. Only surfaceUpdate / dataModelUpdate from
  // the agent triggers a tree rebuild with a new object reference.
  useEffect(() => {
    if (snapshotTreeRef.current === undefined || !loading) return;
    const surface = getSurface(surfaceId);
    if (!surface) {
      clearLoading();
      return;
    }
    if (surface.componentTree !== snapshotTreeRef.current) {
      clearLoading();
    }
  }, [version, surfaceId, loading, getSurface, clearLoading]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const hostStyle: React.CSSProperties =
    node.weight !== undefined ? ({ "--weight": node.weight } as React.CSSProperties) : {};

  return (
    <div className="a2ui-button" style={hostStyle}>
      <button
        onClick={handleClick}
        disabled={loading}
        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all bg-primary text-primary-foreground shadow-[0_1px_2px_0_rgba(0,0,0,0.1),inset_0_1px_0_0_rgba(255,255,255,0.1)] hover:bg-primary/90 hover:shadow-[0_2px_4px_0_rgba(0,0,0,0.12),inset_0_1px_0_0_rgba(255,255,255,0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-9 px-4 py-2"
      >
        {loading && (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
        <ComponentNode node={props.child} surfaceId={surfaceId} />
      </button>
    </div>
  );
});
